import { z } from 'zod'

export interface LeavePolicyFormValues {
  policyName: string
  employeeGroup: string
  annualEntitlement: number
  compensationNature: 'Paid' | 'Unpaid'
  status: boolean
  advancedEnabled: boolean
  workingDays?: number | null
  earnedLeaves?: number | null
  permitCarryForward: boolean
  maxCarryForwardLimit: number
  effectiveFrom: string
  attachmentStagingKey?: string | null
  attachmentName?: string | null
  attachments?: unknown[]
}

/**
 * Returns today's date formatted in local time as "YYYY-MM-DD".
 * Never uses toISOString() to avoid UTC offset / timezone shifts (e.g. in IST).
 */
export function getLocalTodayDateString(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export interface LeavePolicySchemaOptions {
  mode?: 'create' | 'edit'
  initialEffectiveFrom?: string | null
}

export const createLeavePolicySchema = (
  existingPolicies?: { id?: string; name: string }[],
  currentPolicyId?: string,
  options?: LeavePolicySchemaOptions
) => {
  return z
    .object({
      policyName: z
        .string()
        .trim()
        .min(1, 'Policy name is required')
        .max(80, 'Policy name cannot exceed 80 characters'),
      employeeGroup: z
        .string()
        .min(1, 'Select an employee group')
        .uuid('Select an employee group'),
      annualEntitlement: z.coerce
        .number({ message: 'Enter a valid number of entitlement days' })
        .min(0, 'Must be 0 or greater')
        .max(365, 'Cannot exceed 365 days'),
      compensationNature: z.enum(['Paid', 'Unpaid']),
      status: z.boolean(),
      advancedEnabled: z.boolean(),
      workingDays: z
        .preprocess((val) => (val === '' || val === undefined ? null : val), z.any())
        .nullable()
        .optional(),
      earnedLeaves: z
        .preprocess((val) => (val === '' || val === undefined ? null : val), z.any())
        .nullable()
        .optional(),
      permitCarryForward: z.boolean(),
      maxCarryForwardLimit: z.coerce
        .number({ message: 'Must be 0 or greater' })
        .min(0, 'Must be 0 or greater'),
      effectiveFrom: z.string().min(1, 'Select an effective date'),
      attachmentStagingKey: z.string().nullable().optional(),
      attachmentName: z.string().nullable().optional(),
      attachments: z.array(z.any()).optional(),
    })
    .superRefine((data, ctx) => {
      // Effective date validation
      const todayLocal = getLocalTodayDateString()
      const isEditMode = options?.mode === 'edit' || Boolean(currentPolicyId)

      if (!isEditMode) {
        if (!data.effectiveFrom || data.effectiveFrom < todayLocal) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['effectiveFrom'],
            message: 'Effective date cannot be in the past',
          })
        }
      } else {
        const hasDateChanged =
          Boolean(options?.initialEffectiveFrom) &&
          data.effectiveFrom !== options?.initialEffectiveFrom
        if (hasDateChanged && data.effectiveFrom < todayLocal) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['effectiveFrom'],
            message: 'Effective date cannot be in the past',
          })
        }
      }
      // Duplicate name check if existing policies provided
      if (existingPolicies && existingPolicies.length > 0) {
        const normalizedName = data.policyName.trim().toLowerCase()
        const isDuplicate = existingPolicies.some(
          (p) =>
            p.name.trim().toLowerCase() === normalizedName &&
            p.id !== currentPolicyId
        )
        if (isDuplicate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['policyName'],
            message: 'A policy with this name already exists',
          })
        }
      }

      // Advanced Setting validation
      if (data.advancedEnabled) {
        const wdNum =
          data.workingDays !== null && data.workingDays !== undefined
            ? Number(data.workingDays)
            : NaN
        if (
          data.workingDays === null ||
          data.workingDays === undefined ||
          isNaN(wdNum) ||
          wdNum < 0 ||
          wdNum > 366 ||
          !Number.isInteger(wdNum)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['workingDays'],
            message: 'Enter working days between 0 and 366',
          })
        }

        const elNum =
          data.earnedLeaves !== null && data.earnedLeaves !== undefined
            ? Number(data.earnedLeaves)
            : NaN
        if (
          data.earnedLeaves === null ||
          data.earnedLeaves === undefined ||
          isNaN(elNum) ||
          elNum < 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['earnedLeaves'],
            message: 'Enter earned leaves 0 or greater',
          })
        }
      }

      // Carry forward validation
      if (data.permitCarryForward) {
        if (data.maxCarryForwardLimit <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxCarryForwardLimit'],
            message: 'Max carry forward must be greater than 0',
          })
        } else if (data.maxCarryForwardLimit > data.annualEntitlement) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxCarryForwardLimit'],
            message: 'Cannot exceed annual entitlement',
          })
        }
      } else {
        if (data.maxCarryForwardLimit !== 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxCarryForwardLimit'],
            message: 'Must be 0 when carry forward is disabled',
          })
        }
      }
    })
}
